import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'core/store';
import {
  IonBreadcrumb,
  IonBreadcrumbs,
  IonButton,
  IonContent,
  IonIcon,
  IonModal,
  IonPage,
  IonRow,
  IonText,
  IonToolbar
} from '@ionic/react';
import { useHistory } from 'react-router';
import Folder from 'assets/icons/folder.svg';
import { HKPlatform } from 'core/constants';
import { getCleanLabel, getDocumentImageSource } from 'core/util';
import './DocumentsGroups.scss';
import ArrowRight from 'assets/icons/arrow-right.svg';
import ListItem from 'components/items/list-item/ListItem';
import SearchBar from 'components/searchbar/SearchBar';
import LoadingIonList from 'core/layout/loading-ion-list/LoadingIonList';
import BackButtonHeader from 'core/layout/BackButtonHeader';
import {
  hideDocumentsAddModal,
  hideDesktopDocumentEditModal,
  resetDocuments,
  resetSearchedDocuments,
  setSelectedDocument,
  showDesktopDocumentEditModal,
  showDocumentsAddModal
} from 'features/shared/documents/DocumentsSlice';
import {
  fetchDocumentCount,
  fetchSearchedDocuments
} from 'features/shared/documents/DocumentsActions';
import DocumentsAdd from 'features/shared/documents/DocumentsAdd';
import { SearchedDocument } from 'core/types';
import DocumentCard from 'components/cards/document-card/DocumentCard';
import DocumentEdit from 'features/shared/documents/DocumentEdit';

// Exact order for construction phases
const constructionOrder = [
  'Dig, Excavation, & Foundation',
  'Framing',
  'Open Wall, Insulation, & Drywall',
  'Rough Mechanicals',
  'Exterior Finishes',
  'Interior Finishes & Fixtures',
  'Walkthrough & Closing'
];

/**
 * Original Documents > Construction view - construction subfolders only,
 * matching the layout and behavior of other document groups (Floorplans, Manuals, etc.)
 */
const DocumentsConstructionSubfolders: React.FC = () => {
  const {
    loading,
    counts,
    searchedDocuments,
    addModalVisible,
    editDesktopModalVisible
  } = useSelector((state: RootState) => state.documents);
  const homeId = useSelector((state: RootState) => state.home.currentHome?.id);
  const { platformType, isDesktopWidth } = useSelector(
    (state: RootState) => state.platform
  );
  const [search, setSearch] = useState('');
  const history = useHistory();
  const dispatch = useDispatch();

  const getConstructionSubfolders = () => {
    if (!counts) return [];

    return (
      counts
        // Include construction phase subfolders (construction_*) plus the flat
        // 'construction' type itself (e.g. contractor assets added via Switcher
        // that aren't tagged to a specific phase) — getCleanLabel renders its
        // label as "Construction General".
        .filter(
          (item) =>
            item.type === 'construction' ||
            item.type.startsWith('construction_')
        )
        // Sort by construction phase order
        .sort((a, b) => {
          const indexA = constructionOrder.indexOf(getCleanLabel(a.label));
          const indexB = constructionOrder.indexOf(getCleanLabel(b.label));

          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          return getCleanLabel(a.label).localeCompare(getCleanLabel(b.label));
        })
    );
  };

  const generateListOfSearchedGroups = () => {
    const uniqueListOfGroups = new Set<string>();
    searchedDocuments.forEach((document) => {
      if (document.type_label) uniqueListOfGroups.add(document.type_label);
    });
    return Array.from(uniqueListOfGroups);
  };

  const filteredDocumentsGroups = generateListOfSearchedGroups();

  const openDesktopDocument = (document: SearchedDocument) => {
    dispatch(setSelectedDocument(document));
    dispatch(showDesktopDocumentEditModal());
  };

  const showSubfolder = (subfolderType: string) => {
    if (subfolderType !== undefined) {
      dispatch(resetDocuments());
      history.push(`/documents/group/construction/${subfolderType}`);
    }
  };

  const closeAddDocumentsModal = () => {
    if (!!homeId) {
      dispatch(fetchDocumentCount(homeId));
    }
    dispatch(hideDocumentsAddModal());
  };

  useEffect(() => {
    if (!!homeId) {
      dispatch(fetchDocumentCount(homeId));
    }
  }, [homeId, dispatch]);

  useEffect(() => {
    if (!!homeId && !!search) {
      dispatch(fetchSearchedDocuments(homeId, search));
    } else {
      dispatch(resetSearchedDocuments());
    }
  }, [search, editDesktopModalVisible, dispatch]);

  function SearchedDocumentsView() {
    return (
      <>
        {searchedDocuments.length ? (
          <>
            {filteredDocumentsGroups.map((group: string, i: number) => (
              <div
                className={
                  platformType === HKPlatform.DESKTOP && isDesktopWidth
                    ? 'hk-desktop-searched-products'
                    : 'hk-searched-products'
                }
                key={`group-${i}`}
              >
                <h4>{group}</h4>
                {searchedDocuments.map(
                  (searchedDocument: SearchedDocument, j: number) => {
                    const { name, file_url, thumbnail_url, type_label } =
                      searchedDocument;
                    if (type_label !== group) return null;
                    return (
                      <DocumentCard
                        key={`item-${j}`}
                        title={name}
                        image={getDocumentImageSource(
                          file_url,
                          thumbnail_url,
                          undefined,
                          false,
                          type_label
                        )}
                        onClick={() => openDesktopDocument(searchedDocument)}
                      />
                    );
                  }
                )}
              </div>
            ))}
          </>
        ) : (
          <div className="hk-search-empty">
            <h1>No Matches Found</h1>
            <p>
              The thing you are searching for isn't here. Please try another
              search.
            </p>
            <p>
              You can also add a document
              {platformType === HKPlatform.DESKTOP && isDesktopWidth
                ? ' above'
                : ' below'}
              .
            </p>
          </div>
        )}
      </>
    );
  }

  function DesktopView() {
    return (
      <>
        <IonRow className="hk-desktop-digital-documents-group-header">
          <div className="hk-desktop-digital-documents-group-header-container">
            <IonBreadcrumbs className="hk-desktop-digital-documents-group-header-breadcrumbs">
              <IonBreadcrumb onClick={() => history.push('/documents')}>
                Documents
              </IonBreadcrumb>
              <IonBreadcrumb>Construction</IonBreadcrumb>
            </IonBreadcrumbs>
            <div className="hk-desktop-digital-documents-group-header-right ">
              <div className="hk-desktop-digital-documents-group-search">
                <SearchBar
                  value={search}
                  disabled={getConstructionSubfolders()?.length === 0}
                  debounce={1000}
                  onSearchChange={(e) => setSearch(e)}
                />
              </div>
              <div className="hk-desktop-digital-documents-group-header-button">
                <IonButton
                  expand="block"
                  color="primary"
                  strong={true}
                  onClick={() => dispatch(showDocumentsAddModal())}
                >
                  Upload Document
                </IonButton>
              </div>
            </div>
          </div>
        </IonRow>
        <IonContent className="hk-desktop-digital-documents-group">
          <LoadingIonList
            className="hk-desktop-digital-documents-group-list"
            label="Construction Folders"
            items={getConstructionSubfolders()}
            loading={loading}
          >
            {!!search ? (
              <SearchedDocumentsView />
            ) : (
              <>
                {getConstructionSubfolders()?.map(
                  (subfolder: any, i: number) => {
                    const { label, count, type } = subfolder;
                    return (
                      <ListItem
                        key={`item-${i}`}
                        customView={true}
                        onClick={() => showSubfolder(type)}
                      >
                        <IonIcon
                          slot="start"
                          icon={Folder}
                          className="hk-desktop-digital-documents-group-list-item-start-icon"
                        />
                        <div className="hk-desktop-digital-documents-group-list-item-body">
                          <IonText className="hk-desktop-digital-documents-group-list-item-body-title">
                            <h2>{getCleanLabel(label)}</h2>
                          </IonText>
                          <IonText className="hk-desktop-digital-documents-group-list-item-body-description">
                            <p>{count + ' items'}</p>
                          </IonText>
                          <IonIcon
                            slot="end"
                            icon={ArrowRight}
                            className="hk-desktop-digital-documents-group-list-item-body-end-icon"
                          />
                        </div>
                      </ListItem>
                    );
                  }
                )}
              </>
            )}
          </LoadingIonList>
        </IonContent>
      </>
    );
  }

  function MobileView() {
    return (
      <>
        <BackButtonHeader title="Construction" url="/documents" />
        <IonContent fullscreen>
          <SearchBar
            value={search}
            disabled={getConstructionSubfolders()?.length === 0}
            debounce={1000}
            onSearchChange={(e) => setSearch(e)}
          />
          <LoadingIonList
            label="Construction Folders"
            items={getConstructionSubfolders()}
            loading={loading}
          >
            {!!search ? (
              <SearchedDocumentsView />
            ) : (
              <>
                {getConstructionSubfolders()?.map(
                  (subfolder: any, i: number) => {
                    const { label, count, type } = subfolder;
                    return (
                      <ListItem
                        key={`item-${i}`}
                        startIcon={Folder}
                        endIcon={ArrowRight}
                        title={getCleanLabel(label)}
                        description={count + ' items'}
                        onClick={() => showSubfolder(type)}
                      />
                    );
                  }
                )}
              </>
            )}
          </LoadingIonList>
        </IonContent>
        <IonToolbar className="hk-mobile-digital-documents-groups-toolbar">
          <IonButton
            expand="block"
            color="primary"
            strong={true}
            onClick={() => dispatch(showDocumentsAddModal())}
          >
            Add Document
          </IonButton>
        </IonToolbar>
      </>
    );
  }

  return (
    <IonPage
      className={
        platformType === HKPlatform.DESKTOP && isDesktopWidth
          ? 'hk-desktop-digital-documents-group-page'
          : ''
      }
    >
      {platformType === HKPlatform.DESKTOP && isDesktopWidth
        ? DesktopView()
        : MobileView()}
      <IonModal
        className="hk-digital-documents-add-modal"
        isOpen={addModalVisible}
        backdropDismiss={false}
        onDidDismiss={closeAddDocumentsModal}
      >
        <DocumentsAdd />
      </IonModal>
      <IonModal
        className="hk-desktop-digital-documents-edit-modal"
        isOpen={editDesktopModalVisible}
        onDidDismiss={() => dispatch(hideDesktopDocumentEditModal())}
      >
        <DocumentEdit />
      </IonModal>
    </IonPage>
  );
};

export default DocumentsConstructionSubfolders;
